import { Dayjs } from '@seolens/date-manager';
import { Button, Dropdown, Flex, Form, Space, Switch, Tooltip, Typography } from 'antd';
import { FC, useState } from 'react';
import { ChevronDown, QuestionCircle } from 'react-bootstrap-icons';

import { AppDatePicker, ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus, getTodayInTimeZone } from '@/common/utils';

import {
    DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD,
    SEARCH_CONSOLE_ANALYTICS_MORE_PERIODS,
    SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE,
    SEARCH_CONSOLE_ANALYTICS_QUICK_PERIODS,
    SEARCH_CONSOLE_TIME_ZONE,
    SearchConsoleAnalyticsFormFields,
    SearchConsoleAnalyticsFormInputs,
    SearchConsoleAnalyticsPeriod
} from '@/modules/Domains';

interface Props extends AppFormProps<typeof SearchConsoleAnalyticsFormInputs, SearchConsoleAnalyticsFormFields> {
  handleToggleDataState: (checked: boolean) => void;
}

export const SearchConsoleAnalyticsFormView: FC<Props> = ({
  formName,
  form,
  initialValues,
  isFormLoading,
  formErrors,
  validationSchema,
  handleFinish,
  handleFinishFailed,
  handleToggleDataState,
}) => {
  const maxDate = getTodayInTimeZone(SEARCH_CONSOLE_TIME_ZONE);
  const minDate = maxDate.subtract(16, 'months');

  const dataStateFormValue = Form.useWatch([SearchConsoleAnalyticsFormInputs.DataState], form);
  const startDateFormValue = Form.useWatch([SearchConsoleAnalyticsFormInputs.StartDate], form) as Dayjs | undefined;
  const endDateFormValue = Form.useWatch([SearchConsoleAnalyticsFormInputs.EndDate], form) as Dayjs | undefined;

  const [period, setPeriod] = useState<SearchConsoleAnalyticsPeriod>(DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD);

  const morePeriod = SEARCH_CONSOLE_ANALYTICS_MORE_PERIODS.find(({ key }) => key === period);

  const handleChangePeriod = (nextPeriod: SearchConsoleAnalyticsPeriod): void => {
    setPeriod(nextPeriod);

    if (nextPeriod !== 'custom') {
      form.setFieldsValue({
        [SearchConsoleAnalyticsFormInputs.StartDate]: SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE[nextPeriod](maxDate),
        [SearchConsoleAnalyticsFormInputs.EndDate]: maxDate
      });
    }
  };

  return (
    <Form
      form={form}
      name={formName}
      disabled={isFormLoading}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={initialValues}
      autoComplete="off"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <Form.Item
        label={
          <>
            <span>Data state</span>
            &nbsp;
            <Tooltip title="Якщо 'all', дані включатимуть свіжі дані. Якщо 'final', повернуті дані включатимуть лише фіналізовані дані.">
              <QuestionCircle />
            </Tooltip>
          </>
        }
        hasFeedback
        layout="horizontal"
        labelCol={{ span: 'auto' }}
        help={formErrors?.dataState}
        validateStatus={defineInputStatus(isFormLoading, formErrors?.dataState)}
        rules={validationSchema?.dataState}
        name={SearchConsoleAnalyticsFormInputs.DataState}
      >
        <Switch
          checkedChildren="All"
          unCheckedChildren="Final"
          checked={dataStateFormValue === 'all'}
          value={dataStateFormValue === 'all'}
          defaultValue={false}
          onClick={handleToggleDataState}
        />
      </Form.Item>

      <Form.Item label="Період" required>
        <Flex vertical gap={4}>
          <Flex wrap gap={4}>
            {SEARCH_CONSOLE_ANALYTICS_QUICK_PERIODS.map(({ key, label }) => (
              <Button key={key} type={period === key ? 'primary' : 'default'} onClick={() => handleChangePeriod(key)}>
                {label}
              </Button>
            ))}
            <Dropdown
              trigger={['click']}
              disabled={isFormLoading}
              menu={{
                items: SEARCH_CONSOLE_ANALYTICS_MORE_PERIODS,
                selectable: true,
                selectedKeys: [period],
                onClick: ({ key }) => handleChangePeriod(key as SearchConsoleAnalyticsPeriod)
              }}
            >
              <Button type={morePeriod ? 'primary' : 'default'} icon={<ChevronDown />} iconPosition="end">
                {morePeriod?.label ?? 'Докладніше'}
              </Button>
            </Dropdown>
          </Flex>

          {period !== 'custom' && startDateFormValue && endDateFormValue && (
            <Typography.Text type="secondary">
              {startDateFormValue.format('DD.MM.YYYY')} – {endDateFormValue.format('DD.MM.YYYY')}
            </Typography.Text>
          )}
        </Flex>
      </Form.Item>

      <Flex vertical={false} gap={8}>
        <Form.Item
          label="Start Date"
          name={SearchConsoleAnalyticsFormInputs.StartDate}
          hidden={period !== 'custom'}
          hasFeedback
          help={formErrors?.startDate}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.startDate)}
          rules={validationSchema.startDate}
          style={{ width: '50%' }}
        >
          <AppDatePicker allowClear style={{ width: '100%' }} showTime={false} minDate={minDate} maxDate={maxDate} />
        </Form.Item>

        <Form.Item
          label="End Date"
          name={SearchConsoleAnalyticsFormInputs.EndDate}
          hidden={period !== 'custom'}
          hasFeedback
          help={formErrors?.endDate}
          validateStatus={defineInputStatus(isFormLoading, formErrors?.endDate)}
          rules={validationSchema.endDate}
          style={{ width: '50%' }}
        >
          <AppDatePicker allowClear style={{ width: '100%' }} showTime={false} minDate={minDate} maxDate={maxDate} />
        </Form.Item>
      </Flex>

      <Form.Item style={{ marginTop: -10 }}>
        <Typography.Text type="secondary">Усі дати записано за Тихоокеанським часом.</Typography.Text>
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <ButtonSuccess loading={isFormLoading} type="primary" htmlType="submit">
            Виконати запит
          </ButtonSuccess>
        </Space>
      </Form.Item>
    </Form>
  );
};
